import BackendService from 'BackendService';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityLogModelType } from '../../models/ActivityLog';
import {
  emptyModelActivityLogPageFilters,
  getActiveModelActivityLogPageFilters,
  ModelActivityLogPageFilters,
} from './modelActivityLogPageUtils';
import { normalizeActivityLogItem } from './activityLogPageUtils';

export function useModelActivityLogPageList(modelType: ActivityLogModelType, modelId: string) {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [showFilterPanel, setShowFilterPanel] = useState(true);
  const [totalLogs, setTotalLogs] = useState(0);
  const [filters, setFilters] = useState<ModelActivityLogPageFilters>(
    emptyModelActivityLogPageFilters,
  );

  const backendService = useMemo(() => new BackendService(), []);
  const scrollRootRef = useRef<HTMLDivElement>(null);
  const lastLogElementRef = useRef<HTMLDivElement>(null);
  const intersectionObserverRef = useRef<IntersectionObserver | null>(null);
  const inFlightRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const runIdRef = useRef(0);
  const isInitialFiltersEffectRef = useRef(true);
  const filtersRef = useRef(filters);
  const pageRef = useRef(page);
  const hasMoreRef = useRef(hasMore);
  const loadingRef = useRef(loading);
  const loadingMoreRef = useRef(loadingMore);

  filtersRef.current = filters;
  pageRef.current = page;
  hasMoreRef.current = hasMore;
  loadingRef.current = loading;
  loadingMoreRef.current = loadingMore;

  const disconnectObserver = useCallback(() => {
    if (intersectionObserverRef.current) {
      intersectionObserverRef.current.disconnect();
      intersectionObserverRef.current = null;
    }
  }, []);

  const fetchLogs = useCallback(
    async (pageNum: number, append: boolean) => {
      if (!modelType || !modelId) {
        setError('Invalid model type or ID');
        return;
      }
      if (inFlightRef.current) return;

      const isFirstPage = pageNum === 1 && !append;
      const currentRunId = ++runIdRef.current;

      if (abortControllerRef.current) abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();

      inFlightRef.current = true;
      if (isFirstPage) setLoading(true);
      else setLoadingMore(true);

      try {
        const activeFilters = getActiveModelActivityLogPageFilters(filtersRef.current);
        const response = await backendService.getActivityLogs(
          modelType,
          modelId,
          pageNum,
          20,
          activeFilters,
          // @ts-expect-error optional AbortSignal support
          abortControllerRef.current.signal,
        );

        if (currentRunId !== runIdRef.current) return;

        const rawData: any[] = response?.data ?? [];
        const incoming = rawData.map(normalizeActivityLogItem);
        const total = response?.pagination?.total ?? 0;
        const totalPages = response?.pagination?.totalPages ?? 0;

        setLogs((prev) => (append ? [...prev, ...incoming] : incoming));
        setTotalLogs(total);
        setPage(pageNum);
        setHasMore(totalPages > 0 && pageNum < totalPages);
        setError(null);
      } catch (e: any) {
        if (e?.name !== 'AbortError') {
          console.error('Failed to load activity logs:', e, e.stack);
          setError(e?.message || 'Failed to load activity logs');
        }
      } finally {
        if (currentRunId === runIdRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
        inFlightRef.current = false;
      }
    },
    [backendService, modelId, modelType],
  );

  const resetAndFetch = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setPage(1);
    setHasMore(true);
    setLogs([]);
    setError(null);
    fetchLogs(1, false);
  }, [fetchLogs]);

  const attachObserver = useCallback(() => {
    if (!scrollRootRef.current) return;
    if (!lastLogElementRef.current) return;
    if (loadingRef.current || loadingMoreRef.current) return;
    if (!hasMoreRef.current) return;
    if (logs.length === 0) return;

    disconnectObserver();

    intersectionObserverRef.current = new IntersectionObserver(
      (entries) => {
        const [first] = entries;
        if (!first?.isIntersecting) return;
        if (inFlightRef.current) return;
        if (!hasMoreRef.current) return;
        fetchLogs(pageRef.current + 1, true);
      },
      {
        root: scrollRootRef.current,
        rootMargin: '200px',
        threshold: 0,
      },
    );

    intersectionObserverRef.current.observe(lastLogElementRef.current);
  }, [disconnectObserver, fetchLogs, logs.length]);

  useEffect(() => {
    resetAndFetch();
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      disconnectObserver();
    };
  }, [modelType, modelId, resetAndFetch, disconnectObserver]);

  const filtersKey = JSON.stringify(filters);
  useEffect(() => {
    if (isInitialFiltersEffectRef.current) {
      isInitialFiltersEffectRef.current = false;
      return;
    }
    resetAndFetch();
  }, [filtersKey, resetAndFetch]);

  useEffect(() => {
    attachObserver();
    return () => disconnectObserver();
  }, [attachObserver, disconnectObserver, logs.length, hasMore, loading, loadingMore]);

  const handleFilterChange = (field: keyof ModelActivityLogPageFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters(emptyModelActivityLogPageFilters);
  };

  return {
    logs,
    loading,
    loadingMore,
    error,
    totalLogs,
    showFilterPanel,
    setShowFilterPanel,
    filters,
    scrollRootRef,
    lastLogElementRef,
    backendService,
    handleFilterChange,
    clearFilters,
    refreshLogs: resetAndFetch,
  };
}
